import { useMemo } from 'react';
import { Card, Col, Collapse, Empty, Row, Skeleton, Space, Statistic, Typography } from 'antd';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';

import {
	KeyenceValidationStats,
	KeyenceValidationSeverity,
} from '../../interfaces/Quality.Interface';
import { findingCodeLabel } from './constants';

const { Text } = Typography;

const METRIC_FLEX = '0 0 220px';
const METRIC_COL_STYLE = { display: 'flex' } as const;
const METRIC_CARD_STYLE = { width: '100%', height: '100%' };
const METRIC_CARD_STYLES = { body: { padding: '8px 12px', width: '100%' } };
const STAT_STYLES = {
	title: { whiteSpace: 'nowrap' as const, marginBottom: 4 },
	content: { fontSize: 22, lineHeight: 1.2 },
};

const SEVERITY_BAR_COLOR: Record<KeyenceValidationSeverity, string> = {
	Error: '#cf1322',
	Warning: '#d48806',
	Info: '#1677ff',
};

const UPLOADER_BAR_COLOR = '#cf1322';

const CHART_CONTAINER_STYLE = {
	padding: '12px 8px 4px',
	borderRadius: 8,
	background: '#fafafa',
	border: '1px solid #f0f0f0',
} as const;

const TOOLTIP_STYLE = {
	borderRadius: 8,
	border: '1px solid #f0f0f0',
	boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
} as const;

interface Props {
	stats?: KeyenceValidationStats;
	loading: boolean;
}

const MetricCard = ({
	title,
	value,
	color,
}: {
	title: string;
	value: number;
	color?: string;
}) => (
	<Card size="small" style={METRIC_CARD_STYLE} styles={METRIC_CARD_STYLES}>
		<Statistic
			title={title}
			value={value}
			styles={STAT_STYLES}
			valueStyle={{ color: value ? color : undefined }}
		/>
	</Card>
);

const chartHeight = (rows: number, min = 160) => Math.max(min, rows * 36 + 40);

type FindingChartRow = {
	key: string;
	label: string;
	count: number;
	severity: KeyenceValidationSeverity;
};

type UploaderChartRow = {
	key: string;
	label: string;
	failed: number;
	total: number;
};

const StatsPanel = ({ stats, loading }: Props) => {
	const findingChartData = useMemo<FindingChartRow[]>(
		() =>
			stats?.top_finding_codes.map((entry) => ({
				key: `${entry.code}-${entry.severity}`,
				label: findingCodeLabel(entry.code),
				count: entry.count,
				severity: entry.severity as KeyenceValidationSeverity,
			})) ?? [],
		[stats?.top_finding_codes]
	);

	const uploaderChartData = useMemo<UploaderChartRow[]>(
		() =>
			[...(stats?.by_uploader ?? [])]
				.sort((a, b) => b.failed - a.failed || b.total - a.total)
				.filter((u) => u.failed > 0)
				.slice(0, 10)
				.map((u) => ({
					key: String(u.user_id ?? u.name),
					label: u.name,
					failed: u.failed,
					total: u.total,
				})),
		[stats?.by_uploader]
	);

	if (loading && !stats) {
		return (
			<Row gutter={[8, 8]} align="stretch">
				{[0, 1, 2, 3, 4].map((key) => (
					<Col key={key} flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<Card size="small" style={METRIC_CARD_STYLE} styles={METRIC_CARD_STYLES}>
							<Skeleton active title={false} paragraph={{ rows: 2 }} />
						</Card>
					</Col>
				))}
			</Row>
		);
	}
	if (!stats) return null;

	const failed = stats.by_outcome.Failed ?? 0;
	const warned = stats.by_outcome.Warning ?? 0;
	const skipped =
		(stats.by_outcome.Skipped ?? 0) + (stats.by_outcome['Parse Error'] ?? 0);
	const blocked =
		(stats.by_enforcement.Refused ?? 0) +
		(stats.by_enforcement.Overridden ?? 0) +
		(stats.by_enforcement['Accepted Invalid'] ?? 0);

	return (
		<Row gutter={[8, 8]}>
			<Col span={24}>
				<Row gutter={8} align="stretch" wrap={false}>
					<Col flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<MetricCard title="Reports" value={stats.total} />
					</Col>
					<Col flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<MetricCard title="Failed" value={failed} color="#cf1322" />
					</Col>
					<Col flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<MetricCard title="Warnings" value={warned} color="#d48806" />
					</Col>
					<Col flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<MetricCard title="Refused / overridden" value={blocked} color="#d46b08" />
					</Col>
					<Col flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<MetricCard title="Not compared" value={skipped} />
					</Col>
					<Col flex={METRIC_FLEX} style={METRIC_COL_STYLE}>
						<MetricCard
							title="False positives"
							value={stats.false_positives}
							color="#722ed1"
						/>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Card size="small">
					<Collapse
						ghost
						defaultActiveKey={['charts']}
						items={[
						{
							key: 'charts',
							label: 'Issue breakdown',
							children: (
								<Row gutter={[24, 16]}>
									<Col xs={24} lg={14}>
										<Space direction="vertical" size={8} style={{ width: '100%' }}>
											<Text strong>Most common mistakes</Text>
											{findingChartData.length === 0 ? (
												<Empty
													image={Empty.PRESENTED_IMAGE_SIMPLE}
													description="No findings in this range"
												/>
											) : (
												<div style={CHART_CONTAINER_STYLE}>
													<ResponsiveContainer
														width="100%"
														height={chartHeight(findingChartData.length)}
													>
														<BarChart
															data={findingChartData}
															layout="vertical"
															margin={{
																top: 4,
																right: 48,
																bottom: 8,
																left: 8,
															}}
															barCategoryGap="24%"
														>
															<CartesianGrid
																strokeDasharray="3 3"
																horizontal={false}
																stroke="#e8e8e8"
															/>
															<XAxis
																type="number"
																allowDecimals={false}
																tick={{
																	fontSize: 12,
																	fill: 'rgba(0,0,0,0.45)',
																}}
																axisLine={false}
																tickLine={false}
															/>
															<YAxis
																type="category"
																dataKey="label"
																width={168}
																tick={{
																	fontSize: 12,
																	fill: 'rgba(0,0,0,0.75)',
																	fontWeight: 500,
																}}
																axisLine={false}
																tickLine={false}
															/>
															<RechartsTooltip
																cursor={{ fill: 'rgba(0,0,0,0.04)' }}
																contentStyle={TOOLTIP_STYLE}
																formatter={(value: number) => [
																	value,
																	'findings',
																]}
															/>
															<Bar
																dataKey="count"
																name="findings"
																radius={[0, 6, 6, 0]}
																barSize={16}
															>
																{findingChartData.map((row) => (
																	<Cell
																		key={row.key}
																		fill={
																			SEVERITY_BAR_COLOR[row.severity]
																		}
																	/>
																))}
																<LabelList
																	dataKey="count"
																	position="right"
																	style={{
																		fill: 'rgba(0,0,0,0.55)',
																		fontSize: 12,
																		fontWeight: 600,
																	}}
																/>
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
											)}
										</Space>
									</Col>
									<Col xs={24} lg={10}>
										<Space direction="vertical" size={8} style={{ width: '100%' }}>
											<Text strong>Programmers with most issues</Text>
											{stats.by_uploader.length === 0 ? (
												<Text type="secondary">No uploads in this range</Text>
											) : uploaderChartData.length === 0 ? (
												<Empty
													image={Empty.PRESENTED_IMAGE_SIMPLE}
													description="No failed reports in this range"
												/>
											) : (
												<div style={CHART_CONTAINER_STYLE}>
													<ResponsiveContainer
														width="100%"
														height={chartHeight(uploaderChartData.length)}
													>
														<BarChart
															data={uploaderChartData}
															layout="vertical"
															margin={{
																top: 4,
																right: 48,
																bottom: 8,
																left: 8,
															}}
															barCategoryGap="24%"
														>
															<CartesianGrid
																strokeDasharray="3 3"
																horizontal={false}
																stroke="#e8e8e8"
															/>
															<XAxis
																type="number"
																allowDecimals={false}
																tick={{
																	fontSize: 12,
																	fill: 'rgba(0,0,0,0.45)',
																}}
																axisLine={false}
																tickLine={false}
															/>
															<YAxis
																type="category"
																dataKey="label"
																width={120}
																tick={{
																	fontSize: 12,
																	fill: 'rgba(0,0,0,0.75)',
																	fontWeight: 500,
																}}
																axisLine={false}
																tickLine={false}
															/>
															<RechartsTooltip
																cursor={{ fill: 'rgba(0,0,0,0.04)' }}
																contentStyle={TOOLTIP_STYLE}
																content={({ active, payload }) => {
																	if (!active || !payload?.length)
																		return null;
																	const row = payload[0]?.payload as
																		| UploaderChartRow
																		| undefined;
																	if (!row) return null;
																	return (
																		<div
																			style={{
																				background: '#fff',
																				padding: '10px 12px',
																				...TOOLTIP_STYLE,
																			}}
																		>
																			<div
																				style={{
																					fontWeight: 700,
																					marginBottom: 6,
																				}}
																			>
																				{row.label}
																			</div>
																			<div
																				style={{
																					fontSize: 13,
																					lineHeight: 1.55,
																				}}
																			>
																				<div>
																					<strong>{row.failed}</strong>{' '}
																					failed report
																					{row.failed === 1 ? '' : 's'}
																				</div>
																				<div
																					style={{
																						color: 'rgba(0,0,0,0.45)',
																					}}
																				>
																					{row.total} upload
																					{row.total === 1 ? '' : 's'} total
																				</div>
																			</div>
																		</div>
																	);
																}}
															/>
															<Bar
																dataKey="failed"
																name="Failed"
																fill={UPLOADER_BAR_COLOR}
																radius={[0, 6, 6, 0]}
																barSize={16}
															>
																<LabelList
																	dataKey="failed"
																	position="right"
																	style={{
																		fill: 'rgba(0,0,0,0.55)',
																		fontSize: 12,
																		fontWeight: 600,
																	}}
																/>
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
											)}
										</Space>
									</Col>
								</Row>
							),
						},
					]}
					/>
				</Card>
			</Col>
		</Row>
	);
};

export default StatsPanel;
